import { useRoomSettings } from "../context";
import { useCurrentMeeting } from "../../contexts/currentMeeting";
import { useChattoHttpMutation } from "conference/apollo-client/chatto/hooks";
import { CommandType } from "@/api";
import { useParticipantStatus } from "../participantStatus";
import {
    SendParticipantCommandMutation,
    SendParticipantCommandDocument,
    SendParticipantCommandMutationVariables,
} from "../queries-chatto.generated";
import { clearPin, clearTileFitted, isPinned, isTileFitted, setPin, setTileFitted } from "../types";

export const usePinHandler = (participantCname: string, screen: boolean) => {
    const roomSettings = useRoomSettings();

    const pinned = isPinned(roomSettings.pinnedParticipants, participantCname, screen);

    const togglePin = () => {
        if (pinned) clearPin(roomSettings, participantCname, screen);
        else setPin(roomSettings, participantCname, screen);
    };

    return {
        pinned,
        togglePin,
    };
};

export const useMuteHandler = (participantCname: string) => {
    const currentMeeting = useCurrentMeeting();

    const { isMuted } = useParticipantStatus(participantCname);

    const [sendCommand] = useChattoHttpMutation<
        SendParticipantCommandMutation,
        SendParticipantCommandMutationVariables
    >(SendParticipantCommandDocument);

    const toggleMute = async () => {
        await sendCommand({
            variables: {
                command: {
                    roomId: currentMeeting.chatRoomId,
                    type: isMuted ? CommandType.Unmute : CommandType.Mute,
                    initiator: currentMeeting.selfUri,
                    target: participantCname,
                },
            },
        });
    };

    return {
        muted: isMuted,
        toggleMute,
    };
};

export const useUnmuteEnableHandler = (participantCname: string) => {
    const currentMeeting = useCurrentMeeting();

    const { unmuteAllowed } = useParticipantStatus(participantCname);

    const [sendCommand] = useChattoHttpMutation<
        SendParticipantCommandMutation,
        SendParticipantCommandMutationVariables
    >(SendParticipantCommandDocument);

    const toggleUnmuteAllow = async () => {
        await sendCommand({
            variables: {
                command: {
                    roomId: currentMeeting.chatRoomId,
                    type: unmuteAllowed ? CommandType.DisallowUnmute : CommandType.AllowUnmute,
                    initiator: currentMeeting.selfUri,
                    target: participantCname,
                },
            },
        });
    };

    return {
        unmuteAllowed,
        toggleUnmuteAllow,
    };
};

export const useFillModeHandler = (participantCname: string, screen: boolean) => {
    const roomSettings = useRoomSettings();

    const fitted = isTileFitted(roomSettings.tileFittedParticipants, participantCname, screen);

    const toggleFitted = () => {
        if (fitted) clearTileFitted(roomSettings, participantCname, screen);
        else setTileFitted(roomSettings, participantCname, screen);
    };

    return {
        fitted,
        toggleFitted,
    };
};
